import type { CmChecklist } from '@luxuryescapes/contract-svc-offer';

import type {
  DealOptionApprovalRowView,
  DealOptionInclusionRowView,
  DealOptionRowView,
} from '../../tabs/dealOptionsInclusions/types';
import type { WithChecklistItemMetadata } from '../../types';
import { presentRowBase } from '../rowBase';

const UNMAPPED_ROOM_NAME = '—';
const DEFAULT_DEAL_CURRENCY = 'AUD';

export interface PresentedDealOptionsInclusions {
  dealOptionApprovalRows: Array<WithChecklistItemMetadata<DealOptionApprovalRowView>>;
  dealRows: Array<DealOptionRowView>;
  inclusionRows: Array<WithChecklistItemMetadata<DealOptionInclusionRowView>>;
}

export function presentDealOptionsInclusionsRows({
  approvalRows,
  inclusionRows,
}: {
  approvalRows: Array<CmChecklist.DealOptionApprovalRow>;
  inclusionRows: Array<CmChecklist.DealOptionInclusionRow>;
}): PresentedDealOptionsInclusions {
  const dealOptionNames = new Map<string, string>();
  for (const row of [...approvalRows, ...inclusionRows]) {
    if (row.subject.deal_option_name) {
      dealOptionNames.set(row.subject.deal_id, row.subject.deal_option_name);
    }
  }

  const presentedInclusions = inclusionRows.map((row) => ({
    ...presentRowBase(row),
    dealId: row.subject.deal_id,
    dealOptionInclusionRrp: row.evidence.inclusion_rrp,
    dealOptionName: row.subject.deal_option_name ?? '',
    description: row.subject.description ?? '',
    inclusionAssetValue: row.evidence.inclusion_asset_value,
    name: row.subject.inclusion_name ?? '',
    roomName: row.subject.room_name ?? UNMAPPED_ROOM_NAME,
    rrpMatch: row.evidence.rrp_match,
    rrpOccupancyMismatchWith: row.evidence.rrp_occupancy_mismatch_with.map(
      (dealId) => dealOptionNames.get(dealId) ?? dealId,
    ),
    type: row.evidence.inclusion_type ?? '',
  }));

  const inclusionsByDealId = new Map<string, Array<DealOptionInclusionRowView>>();
  for (const inclusion of presentedInclusions) {
    inclusionsByDealId.set(inclusion.dealId, [...(inclusionsByDealId.get(inclusion.dealId) ?? []), inclusion]);
  }

  const presentedApprovals = approvalRows.map((row) => presentRowBase(row));

  return {
    dealOptionApprovalRows: presentedApprovals,
    dealRows: approvalRows.map((row, index) => ({
      ...presentedApprovals[index],
      costPrice: row.subject.cost_price,
      currency: row.subject.currency ?? DEFAULT_DEAL_CURRENCY,
      dealOptionName: row.subject.deal_option_name ?? '',
      inclusions: inclusionsByDealId.get(row.subject.deal_id) ?? [],
      marginPercent: row.subject.margin_percent,
      needsFlexiNightsReview: row.evidence.needs_flexi_nights_review,
      rackRate: row.subject.rack_rate,
      roomAssetLinked: row.subject.room_asset_linked,
      roomName: row.subject.room_name ?? UNMAPPED_ROOM_NAME,
      sellPrice: row.subject.sell_price,
    })),
    inclusionRows: presentedInclusions,
  };
}
